import { type BarItem, BarList } from "@/components/charts/BarList";
import { DonutChart, type DonutSlice } from "@/components/charts/DonutChart";
import { Skeleton } from "@/components/ui/skeleton";
import { type CertificationPersonRow, useCertificationPeople } from "@/hooks/use-certifications";
import { isExpiredDateOnly } from "@/lib/dates";

const EXPIRING_SOON_DAYS = 30;

/** Whole calendar days from the viewer's local today to a UTC-midnight date. */
function daysUntilLocal(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return Number.POSITIVE_INFINITY;
  const target = new Date(y, m - 1, d).getTime();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((target - today) / 86_400_000);
}

interface Buckets {
  lifecycle: { assigned: number; started: number; completed: number };
  expiry: { valid: number; expiringSoon: number; expired: number; noExpiry: number };
}

function tally(rows: CertificationPersonRow[]): Buckets {
  const lifecycle = { assigned: 0, started: 0, completed: 0 };
  const expiry = { valid: 0, expiringSoon: 0, expired: 0, noExpiry: 0 };
  for (const row of rows) {
    lifecycle[row.status] += 1;
    if (!row.expiresAt) {
      expiry.noExpiry += 1;
    } else if (isExpiredDateOnly(row.expiresAt)) {
      expiry.expired += 1;
    } else if (daysUntilLocal(row.expiresAt) <= EXPIRING_SOON_DAYS) {
      expiry.expiringSoon += 1;
    } else {
      expiry.valid += 1;
    }
  }
  return { lifecycle, expiry };
}

/**
 * Status rollup for a certification's holders, shown above the People list.
 * Two views of the same population:
 *   - a donut by lifecycle stage (assigned → started → completed), center =
 *     total holders — categorical, so it uses the brand `--chart-*` series
 *     tokens; and
 *   - a bar list by expiry health (valid / expiring soon / expired / no
 *     expiry) — genuine status, so it uses the semantic status tokens, each
 *     paired with its text label (never color alone).
 *
 * Reuses the People tab's `useCertificationPeople` query (same key → shared
 * cache, one fetch) and computes the counts in the client — no extra endpoint.
 * Renders nothing when the cert has no holders; the list below already shows
 * the empty message.
 */
export function CertificationRollup({ certificationId }: { certificationId: string }) {
  const { data, isLoading, isError } = useCertificationPeople(certificationId);

  if (isLoading) {
    return <Skeleton className="mb-4 h-44 w-full rounded-lg" />;
  }
  // On error, stay silent — the People list below surfaces the failure state.
  if (isError || !data || data.length === 0) {
    return null;
  }

  const { lifecycle, expiry } = tally(data);

  const lifecycleSlices: DonutSlice[] = [
    { label: "Assigned", value: lifecycle.assigned, color: "var(--chart-1)" },
    { label: "Started", value: lifecycle.started, color: "var(--chart-3)" },
    { label: "Completed", value: lifecycle.completed, color: "var(--chart-4)" },
  ];

  const expiryItems: BarItem[] = [
    { label: "Valid", value: expiry.valid, color: "var(--success)" },
    { label: "Expiring soon (≤30d)", value: expiry.expiringSoon, color: "var(--warning)" },
    { label: "Expired", value: expiry.expired, color: "var(--danger)" },
    { label: "No expiry", value: expiry.noExpiry, color: "var(--muted-foreground)" },
  ].filter((item) => item.value > 0);

  return (
    <div className="mb-5 grid grid-cols-1 gap-6 rounded-lg border bg-card p-4 md:grid-cols-2">
      <div>
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          By stage
        </h3>
        <DonutChart
          title="Certification holders by lifecycle stage"
          slices={lifecycleSlices}
          centerLabel={data.length === 1 ? "holder" : "holders"}
        />
      </div>
      <div className="md:border-l md:pl-6">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          By expiry
        </h3>
        {expiryItems.length > 0 ? (
          <BarList title="Certification holders by expiry status" items={expiryItems} />
        ) : (
          <p className="text-sm text-muted-foreground">No expiry dates recorded.</p>
        )}
      </div>
    </div>
  );
}
